import * as React from 'react'
import { WPSClient, APIClient, RequestContext, VideoWizardTitle, VideoWizardViewModel } from 'DVWebNodeUtils'
import { Grid, Packshot, PrimeButton } from 'DVWebNodeUI'
import perlBoolean from '../../utils/perlBoolean'
import { Args } from 'DVWebNodeUtils'
import * as styles from './VideoWizard.module.scss'
import * as cn from 'classnames'

interface HeaderProps extends VideoWizardViewModel {
  selectionCount: number
  closeHandler?: Function
}

enum ModalState {
  INVISIBLE,
  SELECTION_GRID,
  EDUCATIONAL_SCREEN
}

interface VideoWizardState {
  titleList: VideoWizardTitle[]
  selectedTitleIds: Set<string>
  modalState: ModalState
  header?: HeaderProps
  buttonText?: string
  minTitlesToSelect?: number
  forcedWaitDuration?: number
  storefrontReloadPageHandler?: Function
  token?: string
}

interface VideoWizardProps extends Args {
  context: RequestContext
  storefrontReloadPageHandler?: Function
  onComplete: () => void
  onShow: () => void
}

interface FloatingButtonContainerProps {
  buttonText: string
  submitHandler: Function
}

interface SelectionScreenProps {
  state: VideoWizardState
  toggleHandler: Function
  submitHandler: Function
  closeHandler: Function
}

interface EducationalScreenProps extends HeaderProps {
  closeEducationalPageHandler: Function
}

const SELECTION_PLACEHOLDER = '{count}'

const Header = (props: HeaderProps) => <div className={styles.VideoWizardHeader}>
  <div className={styles.VideoWizardHeaderHeadingText}>
    {props.headingText}
    <span className={styles.VideoWizardHeaderSkip} onClick={props.closeHandler}>
      {props.skipText}
    </span>
  </div>
  <div className={styles.VideoWizardHeaderBodyText}>{props.bodyText}</div>
  <div className={styles.VideoWizardHeaderSubheading}>{props.subheadingText}
    <span className={cn(styles.VideoWizardSelection, (props.selectionCount >= props.minTitlesToSelect ?
      styles.VideoWizardSelectionActive : ''))}>
      {props.selectionText.replace(SELECTION_PLACEHOLDER, props.selectionCount.toString())}
    </span>
  </div>
</div>

const FloatingButtonContainer = (props: FloatingButtonContainerProps) => <div
  className={styles.VideoWizardButtonContainer}>
    <PrimeButton className={styles.VideoWizardSubmitButton}
      onClick={props.submitHandler}>
        {props.buttonText}
    </PrimeButton>
</div>

class EducationalScreen extends React.Component<EducationalScreenProps, {}> {

  constructor(props: EducationalScreenProps) {
    super(props)
  }

  componentDidMount() {
    setTimeout(() => {
      this.props.closeEducationalPageHandler()
    }, this.props.forcedWaitDuration)
  }

  render() {
    return (
      <div
        className={styles.VideoWizardEducationalContainer}>
        <div className={styles.VideoWizardEducationalSpinner} />
        <div className={styles.VideoWizardEducationalLoadingText}>{this.props.loadingText}</div>
        <img className={styles.VideoWizardEducationalImage} src={this.props.educationalImage} />
        <div className={styles.VideoWizardEducationalHeader}>{this.props.educationalHeadingText}</div>
        <div className={styles.VideoWizardEducationalBody}>{this.props.educationalBodyText}</div>
      </div>
    )
  }
}

const SelectionScreen = ({state, toggleHandler, submitHandler, closeHandler}: SelectionScreenProps) => <div
  className={styles.VideoWizardSelectionContainer}>
  { state.header && <Header {...state.header} closeHandler={closeHandler}/> }
  <div className={styles.VideoWizardScrollableArea}>
    <div className={styles.VideoWizardGridContainer}>
      <Grid className={styles.VideoWizardGrid}>
        {state.titleList.map((title) =>
          <Packshot
            className={styles.VideoWizardPackshot}
            key={`title-${title.id}`}
            src={title.image}
            alt={title.title}
            overlay={state.selectedTitleIds.has(title.id) &&
              <div className={styles.VideoWizardSelectedOverlay}
                onClick={toggleHandler(title.id)}>
                <div className={styles.VideoWizardOverlayTick}/>
              </div>}
            onClick={toggleHandler(title.id)}/>
          )}
      </Grid>
      { (state.selectedTitleIds.size >= state.minTitlesToSelect) &&
        <FloatingButtonContainer buttonText={state.buttonText}
          submitHandler={submitHandler}/> }
    </div>
  </div>
</div>

export default class VideoWizard extends React.Component<VideoWizardProps & Args, VideoWizardState> {
  wpsClient: WPSClient
  constructor(props: VideoWizardProps) {
    super(props)
    this.wpsClient = new WPSClient(new APIClient)

    this.state = {
      selectedTitleIds: new Set(),
      titleList: [],
      modalState: ModalState.INVISIBLE
    }
  }

  private fetchData() {
    this.wpsClient.VideoWizard({context: this.props.context}).then((videoWizardModel) => {
      if (videoWizardModel &&
          videoWizardModel.viewModel) {
        const isEligible = perlBoolean(videoWizardModel.isEligible)
        this.setState({
          titleList: videoWizardModel.viewModel.titleList,
          selectedTitleIds: new Set(),
          modalState: isEligible ?  ModalState.SELECTION_GRID : ModalState.INVISIBLE,
          header: {
            ...videoWizardModel.viewModel,
            selectionCount: 0
          },
          buttonText: videoWizardModel.viewModel.submitText,
          minTitlesToSelect: videoWizardModel.viewModel.minTitlesToSelect,
          forcedWaitDuration: videoWizardModel.viewModel.forcedWaitDuration,
          token: videoWizardModel.viewModel.token
        })
        this.props.onShow()
      } else {
        this.setState({ modalState: ModalState.INVISIBLE })
        this.props.onComplete()
      }
    })
  }

  componentDidMount() {
    this.fetchData()
  }

  componentDidCatch(error: Error) {
    this.onModalClose()
    this.props.logger.count('DVWeb::VideoWizard::JSFatal', 1, true)
    this.props.logger.error(error, '[DVWeb::VideoWizard] Error while rendering VideoWizard')
    this.props.onComplete()
  }

  toggleSelect = (titleId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const currentSelectedTitleIds = this.state.selectedTitleIds
    if (currentSelectedTitleIds.has(titleId)) {
      currentSelectedTitleIds.delete(titleId)
    } else {
      currentSelectedTitleIds.add(titleId)
    }
    this.setState({
      selectedTitleIds: currentSelectedTitleIds,
      header: {
        ...this.state.header,
        selectionCount: currentSelectedTitleIds.size
      }
    })
  }

  onModalClose = () => {
    this.setState({ modalState: ModalState.INVISIBLE })
    this.props.onComplete()
  }

  skipModal = () => {
    this.onModalClose()
    this.props.logger.count('DVWeb::VideoWizard::ModalSkipped', 1, false)
  }

  submitSelection = async () => {
    const params = {
      context: this.props.context,
      titleIds: Array.from(this.state.selectedTitleIds),
      token: this.state.token
    }
    this.setState({ modalState: ModalState.EDUCATIONAL_SCREEN })

    if (this.state.token) {
      this.wpsClient.SubmitVideoWizard(params)
    }
  }

  closeEducationalPageHandler() {
    this.onModalClose()
    this.props.storefrontReloadPageHandler()
  }

  render() {
    const wrapInContainer = (component: JSX.Element) => <div className={styles.VideoWizardContainer}>
      {component}
    </div>

    return (
      <>
     {this.state.modalState == ModalState.SELECTION_GRID &&
      wrapInContainer(<SelectionScreen state={this.state}
        toggleHandler={this.toggleSelect}
        submitHandler={this.submitSelection}
        closeHandler={this.skipModal}
      />)}
      {this.state.modalState == ModalState.EDUCATIONAL_SCREEN &&
        wrapInContainer(<EducationalScreen {...this.state.header}
          closeEducationalPageHandler={this.closeEducationalPageHandler.bind(this)}
        />)}
      </>
    )
  }
}
